import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useRealmApp } from '../RealmApp';

export default function useUserMutations() {
  const app = useRealmApp();
  return {
    addUser: useAddUser(app.currentUser.id),
    updateUser: useUpdateUser(app.currentUser.id),
    deleteUser: useDeleteUser(app.currentUser.id),
  };
}

const AddUserMutation = gql`
  mutation AddUser($user: UserInsertInput!) {
    addedUser: insertOneUser(data: $user) {
      _id
      address1
      address2
      city
      color
      country
      creationDatetime
      disabled
      email
      image
      lastLogin
      lastName
      name
      owner_id
      password
      phone
      pin
      ref
      roles
      updateDatetime
      creationDatetime
    }
  }
`;

const UpdateUserMutation = gql`
  mutation UpdateUser(
    $userId: ObjectId!
    $ownerId: String!
    $updates: UserUpdateInput!
  ) {
    updatedUser: updateOneUser(
      query: { _id: $userId, owner_id: $ownerId }
      set: $updates
    ) {
      _id
      address1
      address2
      city
      color
      country
      creationDatetime
      disabled
      email
      image
      lastLogin
      lastName
      name
      owner_id
      password
      phone
      pin
      ref
      roles
      updateDatetime
      creationDatetime
    }
  }
`;

const DeleteUserMutation = gql`
  mutation DeleteUser($userId: ObjectId!, $ownerId: String!) {
    deletedUser: deleteOneUser(query: { _id: $userId, owner_id: $ownerId }) {
      _id
    }
  }
`;

const UserFieldsFragment = gql`
  fragment UserFields on User {
    _id
    address1
    address2
    city
    color
    country
    disabled
    email
    image
    lastLogin
    lastName
    name
    owner_id
    password
    phone
    pin
    ref
    roles
    updateDatetime
    creationDatetime
  }
`;

function useAddUser(ownerId) {
  const [addUserMutation] = useMutation(AddUserMutation, {
    // Manually save added Users into the Apollo cache so that User queries automatically update
    // For details, refer to https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
    update: (cache, { data: { addedUser } }) => {
      cache.modify({
        fields: {
          users: (existingUsers = []) => [
            ...existingUsers,
            cache.writeFragment({
              data: addedUser,
              fragment: UserFieldsFragment,
            }),
          ],
        },
      });
    },
  });
  return async (user) => {
    const {
      data: { addedUser },
    } = await addUserMutation({
      variables: {
        user: {
          owner_id: ownerId,
          ...user,
        },
      },
    });
    return addedUser;
  };
}

function useUpdateUser(ownerId) {
  const [updateUserMutation] = useMutation(UpdateUserMutation);
  //
  return async (user, updates) => {
    const {
      data: { updatedUser },
    } = await updateUserMutation({
      variables: { userId: user._id, ownerId, updates },
    });
    return updatedUser;
  };
}

function useDeleteUser(ownerId) {
  const [deleteUserMutation] = useMutation(DeleteUserMutation, {
    update: (cache, { data: { deletedUser } }) => {
      cache.modify({
        fields: {
          users: (existingUsers = [], { readField }) =>
            existingUsers.filter(
              (ref) => deletedUser._id !== readField('_id', ref)
            ),
        },
      });
    },
  });
  return async (user) => {
    const {
      data: { deletedUser },
    } = await deleteUserMutation({
      variables: { userId: user._id, ownerId },
    });
    return deletedUser;
  };
}
